import {useState, useEffect} from 'react';
import {CreateDeliveryForm, DistributionMarketer, DistributionProduct} from '../models/distribution.model';
import {useAuth} from '../../../features/auth/hooks/useAuth';
import {useDistributionUIStore} from '../store/distributionUIStore';

interface UseMarketerDeliveryProps {
  products: DistributionProduct[];
  onSubmit: (formData: CreateDeliveryForm) => Promise<boolean>;
  onFetchAvailableSerials: (productId: string) => Promise<void>;
  onClearAvailableSerials?: () => void;
}

export const useMarketerDelivery = ({
  products,
  onSubmit,
  onFetchAvailableSerials,
  onClearAvailableSerials
}: UseMarketerDeliveryProps) => {
  const {user} = useAuth();

  // Use UI store instead of local state
  const {
    deliveryFormData: formData,
    isSubmittingDelivery: submitting,
    selectedSerials,
    bulkSerials,
    setDeliveryFormData: setFormData,
    setIsSubmittingDelivery: setSubmitting,
    setSelectedSerials,
    setBulkSerials,
    resetDeliveryForm
  } = useDistributionUIStore();

  // Update deliveredBy when user becomes available
  useEffect(() => {
    if (user?.id && !formData.deliveredBy) {
      setFormData(prev => ({
        ...prev,
        deliveredBy: user.id.toString()
      }));
    }
  }, [user?.id, formData.deliveredBy]);

  // Initialize bulk serials when product changes
  useEffect(() => {
    if (formData.productId) {
      const selectedProduct = products.find(p => p.id === formData.productId);

      if (selectedProduct?.hasSerial) {
        setBulkSerials(['']);
        // Clear selectedSerials when switching to a different product
        setSelectedSerials([]);
      } else {
        setBulkSerials([]);
        // Clear selectedSerials if product doesn't have serials
        setSelectedSerials([]);
      }
    }
  }, [formData.productId, products]);

  const handleInputChange = (field: keyof CreateDeliveryForm, value: any) => {
    setFormData({[field]: value});

    if (field === 'productId') {
      const selectedProduct = products.find(p => p.id === value);
      if (value && selectedProduct?.hasSerial) {
        setBulkSerials(['']);
        // Clear selectedSerials when switching to a different product
        setSelectedSerials([]);
        if (value.trim() !== '') {
          onFetchAvailableSerials(value);
        }
      } else {
        setBulkSerials([]);
        // Clear selectedSerials if product doesn't have serials
        setSelectedSerials([]);
        if (!value && onClearAvailableSerials) {
          onClearAvailableSerials();
        }
      }
    }

    if (field === 'quantity' && value) {
      const selectedProduct = products.find(p => p.id === formData.productId);
      if (!selectedProduct?.hasSerial) {
        setSelectedSerials([]);
        setBulkSerials([]);
      }
      // Don't clear selectedSerials for serial products when quantity changes
    }
  };

  const handleSerialToggle = (serialNumber: string) => {
    // Get current selectedSerials and calculate new state
    const currentSerials = Array.isArray(selectedSerials) ? selectedSerials : [];
    const newSelectedSerials = currentSerials.includes(serialNumber)
      ? currentSerials.filter(s => s !== serialNumber)
      : [...currentSerials, serialNumber];

    const totalSelectedSerials = newSelectedSerials.length + bulkSerials.filter(s => s.trim()).length;
    const newQuantity = totalSelectedSerials > 0 ? totalSelectedSerials : 1;

    // Update form data with new quantity
    setFormData(currentFormData => ({
      ...currentFormData,
      quantity: newQuantity
    }));

    // Set the new selectedSerials directly
    setSelectedSerials(newSelectedSerials);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const hasBulkSerials = bulkSerials.filter(s => s.trim()).length > 0;
      const finalSerials = hasBulkSerials ? bulkSerials.filter(s => s.trim()) : selectedSerials;

      const formDataWithSerials = {
        ...formData,
        serials: finalSerials,
        quantity: finalSerials.length > 0 ? finalSerials.length : formData.quantity,
        deliveredBy: user?.id?.toString() || formData.deliveredBy
      };

      const success = await onSubmit(formDataWithSerials);
      if (success) {
        resetForm();
      }
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    resetDeliveryForm();
  };

  const getSelectedProduct = () => {
    return products.find(p => p.id === formData.productId);
  };

  const getSelectedMarketer = (marketers: DistributionMarketer[]) => {
    return marketers.find(m => m.id === formData.marketerId);
  };

  const handleSelectAll = (availableSerials: any[]) => {
    const allSerialNumbers = availableSerials.map(s => s.serialNumber);
    setSelectedSerials(allSerialNumbers);
    const totalSerials = allSerialNumbers.length + bulkSerials.filter(s => s.trim()).length;
    setFormData(prev => ({...prev, quantity: totalSerials}));
  };

  const handleClearSelection = () => {
    setSelectedSerials([]);
    setFormData(prev => ({...prev, quantity: 1}));
  };

  return {
    deliveryFormData: formData,
    isSubmittingDelivery: submitting,
    selectedSerials,
    bulkSerials,
    setBulkSerials,
    handleInputChange,
    handleSerialToggle,
    handleSubmit,
    resetForm,
    getSelectedProduct,
    getSelectedMarketer,
    handleSelectAll,
    handleClearSelection
  };
};
